/**
 * Maps Defender Advanced Hunting tables to the customer-capability flag
 * that gates them, plus a small KQL parser that pulls referenced table
 * names out of a query string. Used by the Custom KQL editor to:
 *
 *   1. Render the capability strip (independent of this map — driven by
 *      the raw flags returned from /api/investigate/{tenantId}/capabilities).
 *   2. Generate contextual hints when a query returns zero rows: if the
 *      KQL references a table the tenant likely can't fully serve, point
 *      that out so the analyst doesn't conclude "no data" when the real
 *      story is "your tenant has Discovery only" or "this preset requires
 *      Defender for Office 365".
 *
 * The Discovery-vs-Full MDCA nuance (see alexein/docs/license-capabilities.md
 * §"Custom KQL query returns 0 rows in 10+ seconds...") is encoded via the
 * PARTIAL_NOTE map below: when the tenant DOES have mdca and the query
 * references CloudAppEvents, a softer "Discovery may only see shadow-IT
 * events" hint fires instead of the harder license-missing one.
 */
import type { HuntingCapability } from '../data/types';

/**
 * Subset of Defender Advanced Hunting tables we care about for license
 * gating. The map deliberately doesn't try to be exhaustive — tables not
 * listed here are treated as "no known gate" and don't produce hints.
 */
const TABLE_REQUIRES: Record<string, HuntingCapability> = {
  // Defender for Endpoint
  DeviceProcessEvents: 'mde',
  DeviceNetworkEvents: 'mde',
  DeviceLogonEvents: 'mde',
  DeviceFileEvents: 'mde',
  DeviceImageLoadEvents: 'mde',
  DeviceRegistryEvents: 'mde',
  DeviceEvents: 'mde',
  DeviceInfo: 'mde',
  DeviceNetworkInfo: 'mde',
  DeviceFileCertificateInfo: 'mde',

  // Defender for Office 365
  EmailEvents: 'mdo',
  EmailAttachmentInfo: 'mdo',
  EmailUrlInfo: 'mdo',
  EmailPostDeliveryEvents: 'mdo',
  UrlClickEvents: 'mdo',

  // Defender for Cloud Apps
  CloudAppEvents: 'mdca',

  // Defender for Identity
  IdentityLogonEvents: 'mdi',
  IdentityDirectoryEvents: 'mdi',
  IdentityQueryEvents: 'mdi',
  IdentityInfo: 'mdi',

  // Entra ID P2 (risk-aware sign-in events)
  AADSignInEventsBeta: 'entraP2',
  AADSpnSignInEventsBeta: 'entraP2',

  // Defender XDR baseline (any Defender product)
  AlertInfo: 'defenderXdrBaseline',
  AlertEvidence: 'defenderXdrBaseline',
};

/**
 * Tables where having the capability flag set is necessary but not
 * sufficient — the tenant may have a partial licence that grants table
 * access but not full event coverage. The value is the analyst-facing
 * hint text we render when 0 rows come back.
 *
 * Today this covers only CloudAppEvents (Discovery-vs-Full distinction
 * documented in alexein/docs/license-capabilities.md §"MDCA nuance —
 * Discovery vs Full"). If other partial-licence cases emerge they should
 * be added here.
 */
const PARTIAL_NOTE: Record<string, string> = {
  CloudAppEvents:
    'Your tenant has Defender for Cloud Apps Discovery (bundled with Business Premium / Entra P2), which permits this table but only populates shadow-IT discovery events — MCAS policy enforcement, OAuth governance, and session-control events require a full MDCA standalone licence.',
};

const CAPABILITY_LABEL: Record<HuntingCapability, string> = {
  mde: 'Defender for Endpoint',
  mdo: 'Defender for Office 365',
  mdca: 'Defender for Cloud Apps',
  mdi: 'Defender for Identity',
  entraP1: 'Entra ID P1',
  entraP2: 'Entra ID P2',
  purviewIrm: 'Purview Insider Risk',
  defenderXdrBaseline: 'Defender XDR',
};

export function capabilityLabel(c: HuntingCapability): string {
  return CAPABILITY_LABEL[c];
}

/**
 * Pull table names out of a KQL query. KQL table references appear as
 * capitalised identifiers at the start of a pipeline. Common patterns:
 *
 *     EmailEvents | where ...
 *     union DeviceProcessEvents, DeviceNetworkEvents
 *     let recent = DeviceInfo | where ...; recent | project ...
 *     materialize(AlertInfo | where ...)
 *
 * We could write a full KQL tokeniser; instead we scan for any token that
 * looks like a Defender table identifier and intersect with the known-table
 * set. This catches false positives that happen to share a name (e.g. a
 * `let DeviceInfo = ...` alias) — acceptable: the hint logic is informational,
 * not blocking.
 */
export function extractKqlTables(kql: string): string[] {
  if (!kql) return [];
  const tokens = kql.match(/\b[A-Z][A-Za-z0-9_]+\b/g) ?? [];
  const found = new Set<string>();
  for (const t of tokens) {
    if (TABLE_REQUIRES[t]) found.add(t);
  }
  return Array.from(found);
}

export interface CapabilityHint {
  severity: 'warning' | 'info';
  table: string;
  requires: HuntingCapability;
  message: string;
}

/**
 * Given a KQL string and the tenant's capability snapshot, produce zero
 * or more hints to show the analyst. Called from KqlEditor when the
 * result returns zero rows.
 *
 * Two hint shapes:
 *   - 'warning': the tenant's snapshot says the required capability is
 *     OFF. Strong hint that the empty result is license-related, not
 *     "no activity".
 *   - 'info':    the tenant has the capability, but the specific table
 *     (currently just CloudAppEvents) is known to need a fuller licence
 *     than the basic flag implies. Softer "may be the cause" wording.
 */
export function generateHints(
  kql: string,
  capabilities: Partial<Record<HuntingCapability, boolean>> | null,
): CapabilityHint[] {
  if (!capabilities) return []; // fail-open when snapshot unavailable

  const tables = extractKqlTables(kql);
  const hints: CapabilityHint[] = [];

  for (const table of tables) {
    const requires = TABLE_REQUIRES[table];
    const hasCap = !!capabilities[requires];

    if (!hasCap) {
      hints.push({
        severity: 'warning',
        table,
        requires,
        message: `\`${table}\` requires ${CAPABILITY_LABEL[requires]} — this tenant's capability snapshot doesn't have that. The empty result is most likely a licence gap rather than no activity.`,
      });
      continue;
    }

    const partialNote = PARTIAL_NOTE[table];
    if (partialNote) {
      hints.push({
        severity: 'info',
        table,
        requires,
        message: `\`${table}\` is queryable for this tenant, but: ${partialNote}`,
      });
    }
  }

  return hints;
}
